import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import React, { type ChangeEvent, useEffect, useState } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import { MonitorUrlFieldInput } from 'components/status-checks/form/status-check/common/MonitorUrlFieldInput'
import { StatusCheckNameField } from 'components/status-checks/form/status-check/common/StatusCheckNameField'
import {
  extractPromQLFromMonitorUrl,
  getAlertUrlQueryParams,
  getPrometheusMonitorUrl,
  PrometheusSelectionSource
} from 'components/status-checks/form/status-check/prometheus/prometheus-monitor-utils'
import { PrometheusMonitorSearch } from 'components/status-checks/form/status-check/prometheus/PrometheusMonitorSearch'
import { useGetExistingStatusCheckByMonitorUrl } from 'components/status-checks/hooks/use-get-existing-status-check-by-monitor-url'
import { type StatusCheckFormValues } from 'components/status-checks/status-checks-form-utils'
import { getStatusChecksUrls } from 'components/status-checks/status-checks-urls-utils'
import { useStatusCheckDetailsContext } from 'context/status-checks/StatusCheckDetailsContext'
import { type PrometheusRule } from 'queries/prometheus/types'

const endpointConfigurationHeaders = {
  'content-type': 'application/x-www-form-urlencoded'
}
const statusChecksUrls = getStatusChecksUrls()
const fieldsToWatch = [
  'externalIntegration',
  'monitorUrl',
  'observabilityTool',
  'prometheusSelector.type',
  'statusCheckName'
] as const

export const PrometheusMonitorSelection: React.FC = () => {
  // This maps to the Rule `query` field, as it is formulaically unique.
  const [selectedRuleId, setSelectedRuleId] = useState<string>()
  const [promQl, setPromQl] = useState<string>()

  const {
    setIsStatusCheckConnectionValidated,
    setIsStatusCheckEvaluationValidated
  } = useStatusCheckDetailsContext()

  const { control, getFieldState, setValue } =
    useFormContext<StatusCheckFormValues>()

  const [
    externalIntegration,
    monitorUrl,
    observabilityTool,
    prometheusSelectorType,
    statusCheckName
  ] = useWatch({ control, name: fieldsToWatch })

  const { name: integrationName, url: integrationUrl } =
    externalIntegration ?? {}
  const { isDirty: isNameFieldDirty } = getFieldState('statusCheckName')

  /*
   * We can extract the encoded query from the `monitorUrl` field value such that
   * both fields can contain an initial value when editing an existing Health Check.
   */
  const [initialPromQL] = useState(
    extractPromQLFromMonitorUrl(monitorUrl) ?? ''
  )

  // Used to create a link to any existing Health Checks when a duplicate monitorUrl is detected.
  const { id: existingStatusCheckId } =
    useGetExistingStatusCheckByMonitorUrl(observabilityTool, monitorUrl) ?? {}

  // Flag derivations to control field display/behavior
  const isRulesSelector =
    prometheusSelectorType === PrometheusSelectionSource.RULES
  const isAlertUrlSelector =
    prometheusSelectorType === PrometheusSelectionSource.ALERT_URL
  const isPromQLSelector =
    prometheusSelectorType === PrometheusSelectionSource.PROMQL

  /*
   * Extract the `query` field from the Prometheus Rule and construct the following:
   * 1. The `monitorUrl` field which appends an encoded PromQL query string for uniqueness.
   * 2. The endpoint payload, which is a non-encoded version of the PromQL query string.
   * 3. (Optional) The Health Check name, if one is not already provided.
   */
  const handleSelectPrometheusRule = ({ name, query }: PrometheusRule) => {
    // `integrationUrl` should never really be undefined here. This is likely a TS modeling issue.
    if (!integrationUrl) return

    setSelectedRuleId(query)

    /*
     * The save function encodes the entire `endpointConfiguration` field.
     * To avoid duplicate encoding, we decode this value here.
     */
    const decodedQuery = decodeURIComponent(query)
    const alertUrl = getPrometheusMonitorUrl(decodedQuery, integrationUrl)
    const payload = getAlertUrlQueryParams(decodedQuery)

    setPromQl(decodedQuery)
    setValue('monitorUrl', alertUrl, { shouldValidate: true })
    setValue('endpointConfiguration.payload', payload)

    // Don't update the Health Check name if a valid one has already been entered.
    if (isNameFieldDirty && statusCheckName?.trim()) return

    // Auto-set the name based on the rule name if one is not already set.
    setValue('statusCheckName', name)
  }

  // Similar to selecting a Prometheus Rule, but we do not attempt to set the Health Check name.
  const handlePromQLFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target

    setSelectedRuleId(undefined)
    setPromQl(value)

    if (!integrationUrl) return

    const alertUrl = getPrometheusMonitorUrl(value, integrationUrl)
    const payload = getAlertUrlQueryParams(value)

    setValue('monitorUrl', alertUrl, { shouldValidate: true })
    setValue('endpointConfiguration.payload', payload)
  }

  // Reset validation flags if the `monitorUrl` field value changes
  useEffect(() => {
    setIsStatusCheckConnectionValidated(false)
    setIsStatusCheckEvaluationValidated(false)
  }, [
    monitorUrl,
    setIsStatusCheckConnectionValidated,
    setIsStatusCheckEvaluationValidated
  ])

  // Reset the selected Rule if the selector type changes.
  useEffect(() => {
    setSelectedRuleId(undefined)
  }, [prometheusSelectorType])

  /*
   * All Prometheus API queries should use the 'POST' method, which deviates from the default
   * form endpoint configuration.
   */
  useEffect(() => {
    setValue('endpointConfiguration.headers', endpointConfigurationHeaders)
    setValue('endpointConfiguration.method', 'POST')
    setValue('endpointConfiguration.payload', '')
  }, [setValue])

  return (
    <Stack spacing={4}>
      <StatusCheckNameField />
      <Stack spacing={2}>
        <Typography>
          What monitor or alert will help Gremlin determine the health of your
          Service?
        </Typography>
        <Controller
          control={control}
          name="prometheusSelector.type"
          render={({ field }) => {
            return (
              <RadioGroup {...field}>
                <Stack spacing={2}>
                  {/* Rules Selector Option */}
                  <FormControl>
                    <Stack spacing={1}>
                      <FormControlLabel
                        control={
                          <Radio value={PrometheusSelectionSource.RULES} />
                        }
                        label="Search Prometheus Rules by name or tags"
                      />
                      {isRulesSelector && integrationName && integrationUrl && (
                        <Box sx={{ pl: 4 }}>
                          <PrometheusMonitorSearch
                            integrationName={integrationName}
                            isDisabled={!isRulesSelector}
                            selectedRowId={selectedRuleId}
                            onSelect={handleSelectPrometheusRule}
                          />
                        </Box>
                      )}
                    </Stack>
                  </FormControl>
                  {/* Alert URL Selector Option */}
                  <FormControl>
                    <Stack spacing={1}>
                      <FormControlLabel
                        control={
                          <Radio value={PrometheusSelectionSource.ALERT_URL} />
                        }
                        label="Provide a Prometheus Alert Rule URL"
                      />
                      <Controller
                        control={control}
                        name="monitorUrl"
                        render={({ field, fieldState }) => (
                          <Box sx={{ pl: 4 }}>
                            <MonitorUrlFieldInput
                              disabled={!isAlertUrlSelector}
                              externalIntegration={externalIntegration}
                              field={field}
                              fieldState={fieldState}
                              {...(existingStatusCheckId && {
                                to: statusChecksUrls.edit(existingStatusCheckId)
                              })}
                            />
                          </Box>
                        )}
                      />
                    </Stack>
                  </FormControl>
                  {/* PromQL Selector Option */}
                  <FormControl>
                    <Stack spacing={1}>
                      <FormControlLabel
                        control={
                          <Radio value={PrometheusSelectionSource.PROMQL} />
                        }
                        label="Enter a PromQL Query"
                      />
                      <Box sx={{ flexGrow: 1, pl: 4 }}>
                        <TextField
                          fullWidth
                          multiline
                          disabled={!isPromQLSelector}
                          rows={3}
                          value={promQl ?? initialPromQL ?? ''}
                          onChange={handlePromQLFieldChange}
                        />
                      </Box>
                    </Stack>
                  </FormControl>
                </Stack>
              </RadioGroup>
            )
          }}
        />
      </Stack>
    </Stack>
  )
}
